import { useCallback, useEffect, useState } from 'react';
import type { AdCreativeResponse, BloomRuntimeConfig } from '@apm/contracts';
import { config } from '../config.js';
import { fetchAdCreative } from '../creative.js';
import { formatPrice } from '@apm/i18n';
import { HANDOFF_IDLE, handoffFromError, handoffFromResponse } from '../handoff.js';
import type { HandoffState } from '../handoff.js';
import { requestTv2mTrigger } from '../tv2m.js';
import { QrOverlay } from '../components/QrOverlay.js';

/**
 * STB 모사 — **다른 사업자의 셋톱 앱이다.** BLOOM 이 아니다 (`docs/14` B11).
 *
 * ── 이 화면이 증명하는 것 ───────────────────────────────────
 *
 * 「한 TV2M 서버가 두 통합 방식을 동시에 서비스하는가」 (`docs/12` §10 의 셋째).
 * 옆 탭의 BLOOM 이 federated 로 BLOOM Server 를 거쳐 핸드오프하는 동안, 이 화면은
 * **BLOOM Server 를 거치지 않고 무인증 `/v1/trigger` 로 곧장** 같은 서버를 부른다.
 * 계정도 로그인도 없고 상품은 TV2M 의 `AdCreative` 가 해석한다.
 *
 * 그래서 이 화면이 통과한다는 것은 **v7 의 경로가 v8 뒤에도 살아 있다는 회귀 증명**이기도
 * 하다 (`docs/14` B11 — "B11 은 앞 단계가 `/v1/trigger` 를 깨뜨리지 않았는지 확인하는 자리").
 *
 * ── 영상이 없다 ─────────────────────────────────────────────
 *
 * `docs/14` B11 은 "샘플 광고 영상을 재생하고" 라고 적었지만 **광고 영상 자산이 이
 * 저장소에 없다.** BLOOM Server 의 HLS 를 틀면 그것은 홈쇼핑 상품 영상이라 Mode A/B 의
 * 경계를 흐린다 — 광고 소재는 TV2M 쪽 것이고 BLOOM Server 는 광고를 모른다는 것이 이
 * 화면의 요지다. 그래서 소재의 썸네일을 광고 화면처럼 깔고, 재생 대신 트리거 경로를
 * 보여 준다.
 * **모사의 요점은 재생이 아니라 「구매 버튼이 무엇을 부르는가」다.**
 *
 * ── 로그인 게이트를 지난 뒤에 그려진다 ──────────────────────
 *
 * 이 화면 자체는 계정을 쓰지 않지만 `App.tsx` 의 게이트 안에 있다. 실제 셋톱이라면
 * 그 게이트가 없는 것이 맞고, 여기서는 **한 앱 안의 모사**라 어쩔 수 없다 — 데모에서
 * 로그인은 이미 끝나 있으므로 장면에는 드러나지 않는다 (`docs/11` §2).
 */
export function StbSimPage({ runtime }: { runtime: BloomRuntimeConfig }) {
  const campaignId = config.simCampaignId;
  const creative = useCreative(runtime.tv2mServerUrl, campaignId);
  const [handoff, setHandoff] = useState<HandoffState>(HANDOFF_IDLE);

  const requestHandoff = useCallback(() => {
    if (creative === null || handoff.kind === 'loading') return;
    setHandoff({ kind: 'loading' });

    void (async () => {
      try {
        /*
         * **BLOOM Server 를 거치지 않는다.** federated 의 `[모바일에서 구매]` 는
         * `POST /v1/bloom/handoff`(BLOOM Server)로 가지만(B9), 위탁형은 TV2M 을 직접
         * 부른다 — 계정이 없으므로 실어 보낼 기기 목록도 없고, 발송도 TV2M 이 자기
         * VAPID 로 한다 (`docs/12` §2.1).
         *
         * `senderDeviceId` 를 싣지 않는다. 셋톱은 TV2M `Device` 로 등록되지 않아 애초에
         * 발송 후보가 아니다 — 절대 규칙 16 이 지키려는 상황이 여기서는 생기지 않는다.
         */
        const response = await requestTv2mTrigger(runtime.tv2mServerUrl, {
          clientType: 'paytv_stb',
          triggerType: 'ad',
          campaignId,
          // 소재가 준 값을 그대로 되돌려 보낸다. **서버는 이 값을 믿지 않는다** —
          // 광고에서 무엇을 파는지 정하는 것은 캠페인이다 (`ad-campaign.resolver.ts`).
          productId: creative.productId,
          receiverDeviceId: 'sim-stb',
        });
        setHandoff(handoffFromResponse(response));
      } catch (error) {
        setHandoff(handoffFromError(error));
      }
    })();
  }, [creative, handoff.kind, runtime.tv2mServerUrl, campaignId]);

  return (
    <>
      {/*
        **모사라는 것을 크게 적는다.** 이 화면이 BLOOM 처럼 보이면 데모에서 「같은 앱이
        광고도 한다」로 읽히는데, 요점은 정확히 그 반대다 — 다른 사업자의 앱이 같은
        TV2M 서버를 쓴다.
      */}
      <div className="sim-banner">
        다른 사업자의 셋톱 앱 모사 — Mode A (위탁형)
        <span className="sim-banner__sub">BLOOM Server 를 거치지 않고 TV2M 을 직접 부른다</span>
      </div>

      {creative === null ? (
        <div className="card">
          <h2 className="card__title">광고 소재를 불러오지 못했습니다</h2>
          <p className="card__text">
            캠페인 <code>{campaignId}</code> 가 TV2M 에 없습니다.
            <br />
            <code>pnpm --filter @apm/tv2m-server db:seed</code> 로 소재를 넣고 다시 여세요.
          </p>
        </div>
      ) : (
        <>
          <div className="sim-screen">
            <img className="sim-screen__plate" src={creative.thumbnailUrl} alt="" />
            <span className="sim-screen__tag">광고</span>
          </div>

          <div>
            <h2 className="product__title">{creative.title}</h2>
            <p className="product__price">
              {/* D12 — 이 화면은 한국어 고정이다. 로케일을 따르지 않는다 */}
              {formatPrice(creative.price, 'ko')}
            </p>
          </div>

          {creative.couponCode !== null && (
            <div className="product__coupon">쿠폰 {creative.couponCode} 적용 대상</div>
          )}

          <div className="spacer" />

          <button
            type="button"
            className="button"
            disabled={handoff.kind === 'loading'}
            onClick={requestHandoff}
          >
            {handoff.kind === 'loading' ? '요청 중…' : '모바일에서 구매'}
          </button>

          <HandoffResult state={handoff} />

          <dl className="kv">
            <dt>campaign</dt>
            <dd>{campaignId}</dd>
            <dt>advertiser</dt>
            <dd>{creative.advertiserId}</dd>
            <dt>상품</dt>
            <dd>{creative.productId}</dd>
            <dt>trigger</dt>
            <dd>{runtime.tv2mServerUrl}/v1/trigger</dd>
          </dl>
        </>
      )}
    </>
  );
}

/**
 * 트리거 결과. **시청 화면과 같은 판정을 쓴다** (`handoffFromResponse`) — 모사라고 다른
 * 규칙을 쓰면 "0대로 보냈습니다" 같은 응답을 여기서만 통과시키게 된다.
 *
 * `sheet` 는 이 화면에서 나올 수 없다. 셋톱은 `mobile_web` 이 아니므로 서버가 Tier 0 을
 * 고르지 않고, 나온다면 `TV2M_FORCE_TIER` 가 켜져 있다는 뜻이다 — 숨기지 않고 그렇게 적는다.
 */
function HandoffResult({ state }: { state: HandoffState }) {
  switch (state.kind) {
    case 'idle':
    case 'loading':
      return null;

    case 'push':
      return <p className="product__note">휴대폰 {state.count}대로 알림을 보냈습니다.</p>;

    case 'qr':
      return (
        <div className="sim-qr">
          <QrOverlay url={state.url} hint="휴대폰으로 스캔하세요" />
        </div>
      );

    case 'sheet':
      return <p className="product__note">서버가 같은 기기 경로를 골랐습니다 (강제 분기?).</p>;

    case 'error':
      return <p className="product__note">{state.reason}</p>;
  }
}

/** 소재를 한 번 읽는다. 재시도하지 않는다 — 없는 캠페인은 두드린다고 생기지 않는다. */
function useCreative(tv2mServerUrl: string, campaignId: string): AdCreativeResponse | null {
  const [creative, setCreative] = useState<AdCreativeResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchAdCreative(tv2mServerUrl, campaignId, controller.signal).then((item) => {
      if (!controller.signal.aborted) setCreative(item);
    });
    return () => {
      controller.abort();
    };
  }, [tv2mServerUrl, campaignId]);

  return creative;
}
